import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { MemberPlacement, QuadrantScores, QuadrantKey } from '../types';

const QUADRANT_LABELS: Record<QuadrantKey, { label: string; short: string; color: string }> = {
  innovation_agility:  { label: 'Innovation & Agility',            short: 'Innovation', color: '#7b1fa2' },
  results_discipline:  { label: 'Results & Discipline',            short: 'Results',    color: '#c62828' },
  process_precision:   { label: 'Process & Precision',             short: 'Process',    color: '#1565c0' },
  teamwork_experience: { label: 'Teamwork & Employee Experience',  short: 'Teamwork',   color: '#2e7d32' },
};

interface TeamMapProps {
  memberPlacements: MemberPlacement[];
  quadrantScores: QuadrantScores;
  size?: number;
}

const TeamMap: React.FC<TeamMapProps> = ({ memberPlacements, quadrantScores, size = 400 }) => {
  const theme = useTheme();
  const half = size / 2;
  const padding = 40;
  const plotArea = size - padding * 2;
  const plotHalf = plotArea / 2;

  const toSafePercent = (value: unknown) => {
    const numeric = typeof value === 'number' ? value : Number(value);
    if (!Number.isFinite(numeric)) return 0;
    return Math.max(0, Math.min(100, numeric));
  };

  const getColor = (quadrant: QuadrantKey) => QUADRANT_LABELS[quadrant].color;

  // Map percentile (1-99) to pixel position within the plot area
  const toX = (acPercentile: number) => padding + (toSafePercent(acPercentile) / 100) * plotArea;
  const toY = (bdPercentile: number) => padding + ((100 - toSafePercent(bdPercentile)) / 100) * plotArea; // invert Y

  return (
    <Box>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ display: 'block' }}>
        {/* Background quadrants */}
        <rect x={padding} y={padding} width={plotHalf} height={plotHalf}
          fill={getColor('teamwork_experience')} opacity={0.08} />
        <rect x={padding + plotHalf} y={padding} width={plotHalf} height={plotHalf}
          fill={getColor('innovation_agility')} opacity={0.08} />
        <rect x={padding + plotHalf} y={padding + plotHalf} width={plotHalf} height={plotHalf}
          fill={getColor('results_discipline')} opacity={0.08} />
        <rect x={padding} y={padding + plotHalf} width={plotHalf} height={plotHalf}
          fill={getColor('process_precision')} opacity={0.08} />

        {/* Quadrant borders */}
        <rect x={padding} y={padding} width={plotArea} height={plotArea}
          fill="none" stroke={theme.palette.divider} strokeWidth={1} />
        <line x1={padding + plotHalf} y1={padding} x2={padding + plotHalf} y2={padding + plotArea}
          stroke={theme.palette.divider} strokeWidth={1} strokeDasharray="4,4" />
        <line x1={padding} y1={padding + plotHalf} x2={padding + plotArea} y2={padding + plotHalf}
          stroke={theme.palette.divider} strokeWidth={1} strokeDasharray="4,4" />

        {/* Quadrant labels */}
        <text x={padding + plotHalf * 0.5} y={padding + 16} textAnchor="middle"
          fontSize={11} fontWeight={600} fill={getColor('teamwork_experience')}>Teamwork &amp; Experience</text>
        <text x={padding + plotHalf * 1.5} y={padding + 16} textAnchor="middle"
          fontSize={11} fontWeight={600} fill={getColor('innovation_agility')}>Innovation &amp; Agility</text>
        <text x={padding + plotHalf * 1.5} y={padding + plotArea - 6} textAnchor="middle"
          fontSize={11} fontWeight={600} fill={getColor('results_discipline')}>Results &amp; Discipline</text>
        <text x={padding + plotHalf * 0.5} y={padding + plotArea - 6} textAnchor="middle"
          fontSize={11} fontWeight={600} fill={getColor('process_precision')}>Process &amp; Precision</text>

        {/* Axis labels */}
        <text x={padding - 4} y={half} textAnchor="end" fontSize={10} fill={theme.palette.text.secondary}
          transform={`rotate(-90 ${padding - 4} ${half})`}>
          High B (Extraversion) &rarr;
        </text>
        <text x={half} y={padding + plotArea + 16} textAnchor="middle" fontSize={10} fill={theme.palette.text.secondary}>
          High A (Dominance) &rarr;
        </text>

        {/* Member dots */}
        {memberPlacements.map((m, i) => {
          const x = toX(m.ac_percentile);
          const y = toY(m.bd_percentile);
          const color = getColor(m.primary_quadrant);
          return (
            <g key={i}>
              <circle cx={x} cy={y} r={m.is_flexor ? 7 : 8}
                fill={color} opacity={0.85} stroke="#fff" strokeWidth={2} />
              <text x={x} y={y - 12} textAnchor="middle" fontSize={9} fontWeight={500}
                fill={theme.palette.text.primary}>
                {m.first_name} {m.last_name.charAt(0)}.
              </text>
            </g>
          );
        })}
      </svg>

      {/* Quadrant score bars */}
      <Box sx={{ mt: 2, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
        {(Object.entries(QUADRANT_LABELS) as [QuadrantKey, typeof QUADRANT_LABELS[QuadrantKey]][]).map(([key, { short, color }]) => {
          const score = toSafePercent(quadrantScores[key]);

          return (
            <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
              <Typography variant="caption" sx={{ minWidth: 70 }}>{short}</Typography>
              <Box sx={{ flex: 1, bgcolor: 'grey.200', borderRadius: 1, height: 8 }}>
                <Box sx={{
                  width: `${Math.round(score)}%`,
                  bgcolor: color,
                  borderRadius: 1,
                  height: '100%',
                  transition: 'width 0.5s ease',
                }} />
              </Box>
              <Typography variant="caption" sx={{ minWidth: 30, textAlign: 'right' }}>
                {Math.round(score)}%
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export { QUADRANT_LABELS };
export default TeamMap;
